import { Box, Divider, styled, Typography } from '@mui/material'

import Anchor from '@ecm/ui/element/Anchor'
import Color from '@ecm/ui/element/Color'
import MainContainer from '@ecm/ui/element/MainContainer'
import DefaultLayout from '@ecm/ui/layout/Default'

type DetailRowProps = {
  children?: React.ReactNode
  label?: string
  value?: string | JSX.Element
}

export const DetailRow = ({ children, label, value }: DetailRowProps) => (
  <tr>
    {label && <th className="table-header">{label}</th>}
    {value && <td>{value}</td>}
    {children && <td>{children}</td>}
  </tr>
)

type DetailTableProps = {
  children: React.ReactNode
  className?: string
  heading: string
  subheading?: string
  noBorder?: boolean
}

export const DetailTable = styled(
  ({ children, className, heading, subheading }: DetailTableProps) => (
    <>
      <Typography variant="h4" component="h3" margin="40px 0 0 0">
        {heading}
      </Typography>
      {subheading && (
        <Typography variant="p3" color={Color.slate.darkest}>
          {subheading}
        </Typography>
      )}
      <table className={className} role="presentation">
        <tbody>{children}</tbody>
      </table>
    </>
  )
)(({ noBorder }) => ({
  '.table-header': {
    alignText: 'left',
    borderBottom: noBorder ? '' : `1px solid ${Color.smoke.darker}`,
    color: Color.slate.darkest,
    fontWeight: 'bold',
    padding: '1.5em 1em 1.5em 0',
    paddingRight: '2em',
    width: '50%',
  },

  td: {
    alignText: 'left',
    borderBottom: noBorder ? '' : `1px solid ${Color.smoke.darker}`,
    color: Color.slate.darkest,
    padding: '1.5em 1em 1.5em 0',
    width: '70%',
  },

  th: {
    borderBottom: noBorder ? '' : `1px solid ${Color.smoke.normal}`,
    padding: '1.5em 1em 1.5em 0',
    textAlign: 'left',
    width: '25%',
  },

  tr: {
    verticalAlign: 'top',
  },

  width: '100%',
}))

type ComplexDetailTableProps = {
  children: React.ReactNode
  className?: string
  heading: string
  subheading?: string
}

export const ComplexDetailTable = styled(
  ({ children, className, heading, subheading }: ComplexDetailTableProps) => (
    <>
      <Typography variant="h4" component="h3" margin="1.5em 0 0.5em 0">
        {heading}
      </Typography>
      {subheading && (
        <Typography variant="p3" color={Color.slate.darkest}>
          {subheading}
        </Typography>
      )}
      <Box
        sx={{
          overflowX: { sm: 'hidden', xs: 'scroll' },
          width: '100% !important',
        }}
      >
        <table className={className}>
          <tbody>{children}</tbody>
        </table>
      </Box>
    </>
  )
)({
  table: {
    display: 'block',
    margin: '0 auto',
    maxWidth: 'fit-content',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
  },
  td: {
    '&.label': {
      color: Color.slate.darkest,
      fontWeight: 'bold',
      minWidth: '150px',
      paddingRight: '0.75em',
      width: '30%',
    },
    '&.plans': {
      minWidth: '100px',
    },

    alignText: 'left',
    borderBottom: `1px solid ${Color.smoke.darker}`,
    color: Color.slate.darkest,
    padding: '1.5em 0.75em 1.5em 0',
    width: '25%',
  },

  th: {
    '&.plans': {
      minWidth: '100px',
    },
    borderBottom: `1px solid ${Color.smoke.darker}`,
    minWidth: '150px',
    padding: '1.5em 0.75em 1.5em 0',
    textAlign: 'left',
  },

  width: '100%',
})

export const PricingDetailPage = () => (
  <DefaultLayout
    seoTitle="Detailed Pricing | Earth Class Mail"
    seoDescription="Detailed Pricing"
    hideFinalCta
  >
    <Box
      py="4em"
      minHeight="15em"
      bgcolor={Color.smoke.normal}
      boxSizing="border-box"
      textAlign="center"
    >
      <Typography variant="h2" component="h1" color={Color.saint.darker} my="14px">
        Detailed Pricing
      </Typography>
    </Box>
    <MainContainer>
      <Box paddingY={{ md: '2em', xs: '1em' }}>
        <Typography variant="h3" component="h2">
          Plans sold after June 1, 2023
        </Typography>
        <Typography variant="p3" color={Color.slate.darkest}>
          If you are subscribed to a virtual mail plan sold on or after June 1, 2023, please consult
          the pricing details below.
        </Typography>
        <DetailTable heading="Addresses">
          <DetailRow
            label="Cost per additional address beyond plan limits"
            value={
              <>
                <b>$30</b> per month
              </>
            }
          />
        </DetailTable>

        <DetailTable heading="Recipients">
          <DetailRow
            label="Cost per additional recipient beyond plan limits"
            value={
              <>
                <b>$5</b> per month
              </>
            }
          />
        </DetailTable>

        <DetailTable heading="Incoming Mail">
          <DetailRow
            label="All plans"
            value={
              <>
                Mail received over plan limits billed as <b>$3</b> per item
              </>
            }
          />
        </DetailTable>

        <DetailTable heading="Content Scans" noBorder>
          <DetailRow
            label="All plans"
            value={
              <>
                You won't be charged extra for scanning as long as your mailed documents are{' '}
                <b>20 pages or less</b>
              </>
            }
          />
        </DetailTable>
        <Box
          sx={{
            backgroundColor: Color.smoke.normal,
            borderRadius: '8px',
            marginBottom: '24px',
            padding: '16px 24px',
          }}
        >
          <Typography variant="p3" color={Color.slate.darkest} m="0" mb={{ md: 0, xs: '1em' }}>
            <b>$0.10</b> per page for black and white scanned documents over the included 20 pages
            per document
          </Typography>
          <Typography variant="p3" color={Color.slate.darkest} m="0">
            <b>$0.15</b> per page for color scanned documents over the included 20 pages per
            document
          </Typography>
        </Box>
        <Divider sx={{ color: Color.smoke.darker }} />
      </Box>

      <Box paddingY={{ md: '2em', xs: '1em' }}>
        <Typography variant="h3" component="h2">
          Standard Fees and Overages for All Plans
        </Typography>

        <DetailTable heading="Mail Storage">
          <DetailRow
            label="All plans"
            value={
              <>
                Free physical letter storage for <b>30 days</b> per mail item, plus free unlimited
                cloud storage of digitized mail.
              </>
            }
          />
          <DetailRow
            label="Letter storage"
            value={
              <>
                <b>$1.50</b> per month after 30 days
              </>
            }
          />
          <DetailRow
            label="Parcel (package) storage"
            value={
              <>
                <b>$15.00</b> per pound per month after 10 days
              </>
            }
          />
        </DetailTable>

        <DetailTable heading="Check Deposits">
          <DetailRow
            label="All plans"
            value={
              <>
                <b>$10</b> per check without check deposit subscription
              </>
            }
          />
          <DetailRow
            label="Check deposit subscription"
            value={
              <>
                <b>$39</b> per month; 5 checks included in plan. Additional check deposits $2 each.
              </>
            }
          />
          <DetailRow
            label="Expedited check deposit requests "
            value={
              <>
                <b>$20</b> per check; complimentary overnight shipping is provided for checks
                deposited into accounts with Bank of America, JPMorgan Chase, and Wells Fargo. All
                other deposits are shipped through USPS.
              </>
            }
          />
        </DetailTable>

        <ComplexDetailTable
          heading="Shipping"
          subheading="We will gladly process forwarding shipments for you that do not exceed 150 pounds in weight and 15.5 inches x 24 inches x 18.5 inches in size."
        >
          <tr>
            <th>Shipping Type</th>
            <th className="plans">Plans</th>
            <th>Shipping Box / Handling</th>
            <th>Box Contents</th>
          </tr>

          <tr>
            <td className="label">Domestic</td>
            <td className="plans">All</td>
            <td>
              <b>$1.25</b> per shipment plus postage
            </td>
            <td>
              <b>$1</b> per item
            </td>
          </tr>

          <tr>
            <td className="label">International</td>
            <td className="plans">All</td>
            <td>
              <b>$5.00</b> per shipment plus postage
            </td>
            <td>
              <b>$1</b> per item
            </td>
          </tr>

          <tr>
            <td className="label">Expedited shipment</td>
            <td className="plans">All</td>
            <td>
              <b>$10</b> per shipment plus postage
            </td>
            <td>
              <b>$1</b> per item
            </td>
          </tr>
        </ComplexDetailTable>

        <DetailTable heading="Special packaging">
          <DetailRow label="Medium box" value={<b>$5</b>} />
          <DetailRow label="Large box" value={<b>$10</b>} />
          <DetailRow
            label="Standard custom packaging"
            value={
              <>
                <b>$12.50</b> per 10 minutes
              </>
            }
          />
          <DetailRow
            label="Expedited custom packaging"
            value={
              <>
                <b>$25</b> per 10 minutes
              </>
            }
          />
        </DetailTable>

        <DetailTable heading="Special requests">
          <DetailRow
            label="Expedited incoming mail item"
            value={
              <>
                <b>$15</b> per item
              </>
            }
          />
          <DetailRow
            label="Expedited scanning"
            value={
              <>
                <b>$15</b> per scanned item
              </>
            }
          />
          <DetailRow
            label="Special Handling"
            value={
              <>
                <b>$12.50</b> per 10 minutes
              </>
            }
          />
          <DetailRow
            label="Heavy incoming mail item"
            value={
              <>
                Items over <b>20lbs</b> will not be accepted
              </>
            }
          />
          <DetailRow
            label="Oversized package"
            value={
              <>
                Items exceeding <b>19" x 14" x 12"</b> will not be accepted
              </>
            }
          />
        </DetailTable>

        <DetailTable heading="Secure shredding">
          <DetailRow label="All plans" value="Free" />
        </DetailTable>
      </Box>

      <Box paddingY={{ md: '2em', xs: '1em' }}>
        <Typography variant="h4" component="h3">
          Cancellation and Reactivation Fees
        </Typography>
        <Typography variant="p3" color={Color.slate.darkest}>
          All plans carry an associated $50 cancellation fee that covers the cost of handling your
          mail for an additional 6 months after closing an account.
        </Typography>
        <Typography variant="p3" color={Color.slate.darkest}>
          A reactivation fee of $99 applies to reactivating a closed account. Reactivation is only
          available for accounts closed within 90 days.
        </Typography>
      </Box>

      <Box
        sx={{
          a: {
            color: Color.slate.darkest,
          },
          backgroundColor: Color.smoke.normal,
          borderRadius: '8px',
          color: Color.slate.darkest,
          marginBottom: '24px',
          padding: '16px 28px',
          textAlign: 'center',
        }}
      >
        <Typography variant="p3" color={Color.slate.darkest} m="0">
          For plans sold prior to June 1, 2023, please see{' '}
          <Anchor href="/pricing/2020details">detailed pricing here.</Anchor>
        </Typography>
      </Box>
    </MainContainer>
    <Divider
      sx={{ color: Color.smoke.darker, display: { md: 'block', xs: 'none' }, marginTop: '120px' }}
    />
  </DefaultLayout>
)

export default PricingDetailPage
